import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDownIcon } from 'lucide-react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Card,
  CardHeader,
  Command,
  CommandGroup,
  CommandItem,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@ais-ui/ui-core';
import { capitalize, cn, isPresent } from '@ais-ui/utils';

import { useLocalization } from '@ais-ui/locale-management';

import {
  PiAnchor,
  PiCaretLeftLight,
  PiCaretRightLight,
  PiDotsThreeVerticalBold,
  PiFileCsv,
  PiX,
} from 'react-icons/pi';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { useAISMapContext } from '../Map/AISMapContext';
import { useHorizontalScroll } from '../Reports/Table/useHorizontalScroll';
import {
  useFetchTrackById,
  usePortArrivals,
  useTracksGetList,
} from '@ais-ui/api'; // Assuming this is your existing hook
import { useRoutes } from './RoutesContext';
import { ErrorDisplayTable } from '../../components/ErrorDisplayTable';
import { EmptyListTable } from '../../components/EmptyListTable';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { type Ship, TrackResult } from '@ais-ui/api-spec/portserver';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { ToString } from '@formatjs/ecma402-abstract';
import { CreateRoute } from './Mutations/CreateRoute';
import { DeleteRoute } from './Mutations/DeleteRoute';
import { shapeToIcon } from '../Shapes/shapeToIcon';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';
import { useSelectionState } from '../../components/SelectionStateProvider';
import { shapeToVectorLayer, useLayers } from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface TrackFetchAndDrawProps {
  id: number;
  shipName?: string;
  mmsi?: number;
}

const useTrackFetchAndDraw = ({
  id,
  shipName,
  mmsi,
}: TrackFetchAndDrawProps) => {
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);

  const { addLayer, zoomToLayer } = useLayers();
  const { messages } = useLocalization();
  const { toast } = useToast();

  const fetchTrackById = useFetchTrackById();

  const trackId = useMemo(() => {
    return `track-${id}`;
  }, [id]);

  const onFetchTrack = async () => {
    setIsLoadingTrack(true);
    toast({
      title: messages.genericToastTitleStarted,
      description: 'Started fetching track...',
    });

    try {
      const result = await fetchTrackById({ id, track: 1 });

      if (result && result.track && result.track.coordinates) {
        console.log('shipname', shipName);

        const shipNameWithFallback =
          shipName ?? mmsi?.toString() ?? `Ship ${id}`;
        const name = `${shipNameWithFallback}'s track`;

        const trackGeoJson: GeoJson = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: result.track.coordinates,
          },
          properties: {
            id: trackId,
            name: name,
            shipName: result.source?.ship?.name || shipName,
            mmsi: result.source?.ship?.mmsi || mmsi,
            callsign: result.source?.ship?.callsign,
            shiptype: result.source?.ship?.shiptype,
            destination: result.destination?.port?.name,
            eta: result.eta,
            sog: result.source?.ship?.sog,
            color: 'yellow',
          },
        };

        const layer = shapeToVectorLayer(trackGeoJson);

        addLayer(layer, name, true, true, true, undefined, trackId);
        zoomToLayer(layer);

        toast({
          title: messages.genericToastTitleSuccess,
          description: 'Successfully fetched track',
        });

        return result;
      } else {
        console.warn(
          'Track data is missing or incomplete in the fetch result.'
        );
        toast({
          title: messages.genericToastTitleError,
          description: 'No track data available',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching track:', error);
      toast({
        title: messages.genericToastTitleError,
        description: 'Failed to fetch track',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTrack(false);
    }
  };

  return {
    onFetchTrack,
    isLoadingTrack,
    trackId,
  };
};
interface RoutesTableProps {
  className?: string;
}

const RoutesDropDown = () => {
  const [shiplistName, setShiplistName] = useState('');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const { messages } = useLocalization();

  const onDummy = () => {
    console.log('DUMMY!');
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="m-0">
          Actions <ChevronDownIcon className="h-4 w-4 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72">
        <Command>
          <CommandGroup heading="Actions">
            <CommandItem onSelect={onDummy}>
              <PiFileCsv className="mr-2 h-4 w-4" />
              {messages.targetsOnMapExportCsvText}
            </CommandItem>
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Actions cell component for the table
interface RoutesTableActionsProps {
  arrival: TrackResult;
}

const RoutesTableActions = ({ arrival }: RoutesTableActionsProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { setZoomToShip } = useAISMapContext();

  const { onFetchTrack, isLoadingTrack } = useTrackFetchAndDraw({
    id: arrival.id as number,
    shipName: arrival.source?.ship?.name,
    mmsi: arrival.source?.ship?.mmsi,
  });

  const onGoToTarget = useCallback(
    (ship?: Ship) => {
      if (!ship) {
        toast({
          title: messages.genericToastTitleError,
          description: 'No ship data available',
          variant: 'destructive',
        });
        return;
      }

      const autoCompleteShip: AutoCompleteShipInfo = {
        name: ship.name ? ship.name : null,
        mmsi: ship.mmsi ? ToString(ship.mmsi) : null,
        imo: ship.imo ? ToString(ship.imo) : null,
        callSign: ship.callsign ? ship.callsign : null,
        type: 'TT_SHIP',
      };
      setZoomToShip(autoCompleteShip);
    },
    [messages, setZoomToShip, toast]
  );

  const onDelete = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id={`${arrival?.source?.ship?.name || 'unknown'}-dropdown-button`}
            variant="ghost"
            className="h-8 w-8 p-0"
          >
            <PiDotsThreeVerticalBold className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onGoToTarget(arrival?.source?.ship)}>
            View target
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onFetchTrack()}>
            View route
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete}>Delete route</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showDeleteDialog && (
        <DeleteRoute
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
          route={arrival}
        />
      )}
    </>
  );
};

// Create sortable header component
interface SortableHeaderProps {
  column: any;
  children: React.ReactNode;
}

const SortableHeader = ({ column, children }: SortableHeaderProps) => {
  if (!column.getCanSort()) {
    return <div className="font-medium">{children}</div>;
  }

  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      className="h-auto p-0 font-medium hover:bg-transparent justify-start"
    >
      {children}
      {column.getIsSorted() === 'desc' ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : column.getIsSorted() === 'asc' ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
};

export const RoutesTable = memo(({ className }: RoutesTableProps) => {
  const { tableExpanded, setTableExpanded, setCenterComponent } =
    useMapPanelContext();
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const {
    shape,
    showDialog: showCreateDialog,
    setShowDialog: setShowCreateDialog,
  } = useRoutes();

  const { currentPort } = useSelectionState();

  // TanStack Table sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    data: portData,
    isLoading: isPortLoading,
    isError: isPortError,
  } = usePortArrivals(currentPort?.id ? Number(currentPort.id) : undefined);

  const {
    data: trackData,
    isLoading: isTrackLoading,
    isError: isTrackError,
  } = useTracksGetList(!!shape);

  const isLoading = useMemo(() => {
    if (currentPort) {
      return isPortLoading;
    } else if (shape) {
      return isTrackLoading;
    }
    return false;
  }, [isPortLoading, isTrackLoading, currentPort, shape]);

  const isError = useMemo(() => {
    if (currentPort) {
      return isPortError;
    } else if (shape) {
      return isTrackError;
    }
  }, [isPortError, isTrackError, currentPort, shape]);

  const ShapeIcon = useMemo(() => {
    if (shape) {
      return shapeToIcon(shape);
    }
    return null;
  }, [shape]);

  const arrivals = useMemo(() => {
    if (portData && portData?.records) {
      return portData.records;
    }
    if (trackData && trackData?.records) {
      return trackData.records.filter(
        (record) => record.destination?.location?.id === shape?.properties?.id
      );
    }
    return [];
  }, [portData, shape?.properties?.id, trackData]);

  const columns = useMemo<ColumnDef<TrackResult>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        cell: ({ row }) => <div className="py-1">{row.index + 1}</div>,
        enableSorting: false,
        size: 50,
      },
      {
        id: 'name',
        accessorFn: (row) => row?.source?.ship?.name || '',
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderName}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="font-medium py-1">
            {capitalize(
              row.original?.source?.ship?.name
                ? row.original.source.ship.name
                : 'unknown'
            )}
          </div>
        ),
      },
      {
        id: 'mmsi',
        accessorFn: (row) => row?.source?.ship?.mmsi || 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderMmsi}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.mmsi
              ? row.original.source.ship.mmsi
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'imo',
        accessorFn: (row) => row?.source?.ship?.imo || 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderImo}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.imo
              ? row.original.source.ship.imo
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'callsign',
        accessorFn: (row) => row?.source?.ship?.callsign || '',
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderCallsign}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.callsign
              ? row.original.source.ship.callsign
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'shiptype',
        accessorFn: (row) => row?.source?.ship?.shiptype || '',
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderShipType}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.shiptype
              ? row.original.source.ship.shiptype
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'draught',
        accessorFn: (row) => row?.source?.ship?.draught || 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderDraught}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.draught
              ? `${row.original.source.ship.draught} m`
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'width',
        accessorFn: (row) => row?.source?.ship?.width || 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderWidth}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.width
              ? `${row.original.source.ship.width} m`
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'length',
        accessorFn: (row) => row?.source?.ship?.length || 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderLength}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.length
              ? `${row.original.source.ship.length} m`
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'sog',
        accessorFn: (row) => row?.source?.ship?.sog ?? 0,
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderSog}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {isPresent(row.original?.source?.ship?.sog)
              ? `${row.original.source.ship.sog} knots`
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'country',
        accessorFn: (row) => row?.source?.ship?.country || '',
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderCountry}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.source?.ship?.country
              ? `${row.original.source.ship.country}`
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'message',
        accessorFn: (row) => row?.message || '',
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderState}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.message ? `${row.original.message}` : ''}
          </div>
        ),
      },
      {
        id: 'eta',
        accessorFn: (row) => (row?.eta ? new Date(row.eta).getTime() : 0),
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderEtaCalculated}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.eta
              ? formatDateTimeWithTimeZone(row.original.eta)
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'etaErp',
        accessorFn: (row) => (row?.etaErp ? new Date(row.etaErp).getTime() : 0),
        header: ({ column }) => (
          <SortableHeader column={column}>
            {messages.RoutesTableHeaderEtaErp}
          </SortableHeader>
        ),
        cell: ({ row }) => (
          <div className="py-1">
            {row.original?.etaErp
              ? formatDateTimeWithTimeZone(row.original.etaErp)
              : 'unknown'}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="py-1">
            <RoutesTableActions arrival={row.original} />
          </div>
        ),
        enableSorting: false,
      },
    ],
    [messages, formatDateTimeWithTimeZone]
  );

  // Memoize data for stability
  const memoizedArrivals = useMemo(() => arrivals, [arrivals]);

  // TanStack Table instance
  const table = useReactTable({
    data: memoizedArrivals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const viewportRef = useRef<HTMLDivElement | null>(null);

  const handleClose = () => {
    setCenterComponent(CENTERCOMPONENTS.NONE);
  };

  // Use an effect to check scroll state after data loads and component renders
  useEffect(() => {
    if (!isLoading && arrivals.length > 0) {
      // Add a small delay to allow DOM to fully render the table
      const timer = setTimeout(() => {
        // Manually trigger the scroll check
        if (viewportRef.current) {
          // Dispatch a scroll event to force recalculation
          viewportRef.current.dispatchEvent(new Event('scroll'));
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, arrivals.length]);

  const { handleScroll, showLeftScrollButton, showRightScrollButton } =
    useHorizontalScroll(viewportRef);

  if (!tableExpanded) {
    return null;
  }

  return (
    <Card
      className={cn(
        'relative flex flex-col h-fit w-full transition-all group/data-table px-1 pb-4 pt-1',
        className
      )}
    >
      <Button
        onClick={handleClose}
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 z-10 h-8 w-8 p-0 hover:bg-secondary"
      >
        <PiX className="h-4 w-4" />
      </Button>
      <CardHeader className="px-7 py-4 pb-2 pr-12 text-lg font-normal text-muted-foreground">
        <div className="flex justify-between items-start">
          <div>
            {currentPort && (
              <div className="flex space-x-2">
                <PiAnchor className="h-6 w-6 flex-shrink-0"></PiAnchor>
                <h3>{currentPort.name}</h3>
              </div>
            )}
            {shape && ShapeIcon && (
              <div className="flex space-x-2">
                <ShapeIcon className="h-6 w-6 flex-shrink-0" />
                <h3>
                  {' '}
                  {shape?.properties?.name
                    ? shape.properties.name
                    : 'Unknown shape'}
                </h3>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Clear sorting button */}
            {sorting.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSorting([])}
                className="text-xs"
              >
                Clear Sort
              </Button>
            )}
            <Button onClick={() => setShowCreateDialog(true)} className="mt-1">
              Create
            </Button>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {table.getRowModel().rows.length}{' '}
          {table.getRowModel().rows.length === 1 ? 'row' : 'rows'}
        </div>
      </CardHeader>
      <div className="flex overflow-hidden">
        <Button
          onClick={() => handleScroll(-200)}
          className={cn(
            'p-0 mr-1',
            !showLeftScrollButton && 'opacity-0 pointer-events-none'
          )}
        >
          <PiCaretLeftLight className="h-4 w-4" />
        </Button>
        <ScrollArea
          infiniteScrollRef={viewportRef}
          observeResize={false}
          className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
        >
          <Table>
            <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow className="border-0">
                  <TableCell colSpan={table.getAllColumns().length}>
                    <div className="flex justify-center items-center min-h-[100px]">
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow className="border-0">
                  <TableCell colSpan={table.getAllColumns().length}>
                    <div className="flex justify-center items-center min-h-[100px]">
                      <ErrorDisplayTable
                        srcImage="/422_not_supported_error_bro.png"
                        message="Could not fetch routes"
                        className=""
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !isError &&
                table.getRowModel().rows.length === 0 && (
                  <TableRow className="border-0">
                    <TableCell colSpan={table.getAllColumns().length}>
                      <div className="flex justify-center items-center min-h-[100px]">
                        <EmptyListTable
                          srcImage="empty_list_bro.png"
                          entity="route"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {!isLoading &&
                !isError &&
                table.getRowModel().rows.length > 0 &&
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="odd:bg-gray-50">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </ScrollArea>
        <Button
          onClick={() => handleScroll(200)}
          className={cn(
            'p-0 ml-1',
            !showRightScrollButton && 'opacity-0 pointer-events-none'
          )}
        >
          <PiCaretRightLight className="h-4 w-4" />
        </Button>
      </div>
      {showCreateDialog && <CreateRoute />}
    </Card>
  );
});

RoutesTable.displayName = 'RoutesTable';
